import { memo, useMemo } from 'react';

import { AVATAR_COLORS } from '@app/constants/avatarColors';

import type { CSSProperties } from 'react';

export interface AvatarProps {
  src?: string | null;
  className?: string | null;
  size?: number | string;
  nameFirst?: string | null;
  nameLast?: string | null;
  round?: boolean;
  style?: CSSProperties;
  'data-testid'?: string;
}

const getInitials = (
  nameFirst?: string | null,
  nameLast?: string | null
): string => {
  const first = nameFirst?.trim()?.[0] ?? '';
  const last = nameLast?.trim()?.[0] ?? '';

  if (last && first) return `${last}${first}`.toUpperCase();
  if (first) return first.toUpperCase();
  if (last) return last.toUpperCase();
  return '';
};

const getColorByName = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const parseSizePx = (size: number | string): number => {
  if (typeof size === 'number') return size;
  return parseInt(size, 10) || 40;
};

export const Avatar = memo(
  ({
    src,
    className,
    size = 40,
    nameFirst,
    nameLast,
    round = true,
    style,
    ...rest
  }: AvatarProps) => {
    const sizePx = parseSizePx(size);
    const fontSize = Math.max(sizePx * 0.4, 10);

    const baseStyle: CSSProperties = useMemo(
      () => ({
        width: sizePx,
        height: sizePx,
        minWidth: sizePx,
        minHeight: sizePx,
        borderRadius: round ? '50%' : 4,
        ...style,
      }),
      [sizePx, round, style]
    );

    if (src) {
      const imgSrc = src.startsWith('data:')
        ? src
        : `data:image/png;base64,${src}`;

      return (
        <img
          className={className ?? undefined}
          src={imgSrc}
          alt="avatar"
          style={{ ...baseStyle, objectFit: 'cover', display: 'block' }}
          data-testid={rest['data-testid'] ?? 'avatar-icon'}
        />
      );
    }

    const initials = getInitials(nameFirst, nameLast);
    const fullName = [nameLast, nameFirst].filter(Boolean).join(' ') || '?';
    const bgColor = getColorByName(fullName);

    return (
      <div
        className={className ?? undefined}
        style={{
          ...baseStyle,
          backgroundColor: bgColor,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize,
          fontWeight: 600,
          lineHeight: 1,
          userSelect: 'none',
          flexShrink: 0,
        }}
        title={fullName}
        data-testid={rest['data-testid'] ?? 'avatar-icon'}
      >
        {initials}
      </div>
    );
  }
);
